type GpsTrackerLocationCsvRow = Record<string, unknown>

type BuildGpsTrackerLocationCsvDataParams = {
  deviceToken: string
  name: string
  gpsLogs: IJT808Log[]
  wifiLocations: IGpsTrackerWifiLocation[]
}

type GpsTrackerLocationCsvData = {
  rows: GpsTrackerLocationCsvRow[]
  fields: string[]
}

const BASE_FIELDS = [
  'device_token',
  'name',
  'latitude',
  'longitude',
  'speed',
  'source',
  'timestamp',
  'has_fix',
  'accuracy_m',
]

const EXCLUDED_FIELDS = new Set([
  'provider',
  'access_point_count',
  'observation_id',
  'wifi_geolocation_cache_id',
])

const getTimestampTime = (timestamp: unknown): number | null => {
  if (typeof timestamp !== 'string' || !timestamp) return null

  const time = new Date(timestamp).getTime()
  return Number.isFinite(time) ? time : null
}

const sortRowsByTimestamp = (
  rows: GpsTrackerLocationCsvRow[],
): GpsTrackerLocationCsvRow[] => rows
  .map((row, index) => ({
    row,
    index,
    time: getTimestampTime(row.timestamp),
  }))
  .sort((first, second) => {
    if (first.time === null && second.time === null) return first.index - second.index
    if (first.time === null) return 1
    if (second.time === null) return -1
    return first.time - second.time || first.index - second.index
  })
  .map(({ row }) => row)

const buildGpsTrackerLocationCsvData = ({
  deviceToken,
  name,
  gpsLogs,
  wifiLocations,
}: BuildGpsTrackerLocationCsvDataParams): GpsTrackerLocationCsvData => {
  const gpsRows = gpsLogs.map<GpsTrackerLocationCsvRow>((log) => ({
    ...log,
    source: 'gps',
    device_token: deviceToken,
    name,
    timestamp: log.timestamp || '',
    latitude: log.latitude ?? '',
    longitude: log.longitude ?? '',
    speed: log.speed ?? '',
    has_fix: log.has_fix ?? log.hasFix ?? '',
    accuracy_m: '',
  }))
  const wifiRows = wifiLocations.map<GpsTrackerLocationCsvRow>((location) => ({
    source: 'wifi',
    device_token: deviceToken,
    name,
    timestamp: location.timestamp || '',
    latitude: location.latitude ?? '',
    longitude: location.longitude ?? '',
    speed: '',
    has_fix: '',
    accuracy_m: location.accuracy_m ?? '',
  }))
  const rows = sortRowsByTimestamp([...gpsRows, ...wifiRows])
  const fields = [...BASE_FIELDS]

  rows.forEach((row) => {
    Object.keys(row).forEach((field) => {
      if (!fields.includes(field) && !EXCLUDED_FIELDS.has(field)) {
        fields.push(field)
      }
    })
  })

  return { rows, fields }
}

export default buildGpsTrackerLocationCsvData
